<template>
    <div>
        <Header />
        <router-view />
        <Footer />
        <alert-dialog ref="alertDialog"></alert-dialog>
    </div>
</template>

<style lang="scss">
    #app {
        font-family: var(--bs-font-sans-serif);
        color: var(--bs-body-color);
        background-color: var(--bs-body-bg);
    }
</style>

<script lang="ts">

// not yet import "@progress/kendo-theme-default/dist/all.css";

import { defineComponent } from "vue";

import { SignalR } from "./support/SignalR";
import Tooltip from "@/shared/support/Tooltip";
import Header from "@/components/Header.vue";
import Footer from "@/shared/components/common/Footer.vue";
import AlertDialog, { confirmOk, confirmYesNo, initializeAlertDialog } from "@/shared/components/common/AlertDialog.vue";
import { DataRequest } from "./shared/support/Data";
import { Access, CurrentUser } from "./identity";
import { Global } from "./support/GlobalData";
import { ButtonClicked } from "./shared/enums/ButtonClicked";
import { CalibrationEventSupport } from "./support/CalibrationEvent";
import { CalibrationEvent } from "./shared/enums/CalibrationEvent";
import Utility from "./shared/support/Utility";
import Toast from "./shared/support/Toast";
import DropDownMenuSupport from "./shared/support/DropDownMenu";
import { CalibrationSessionDto } from "./shared/models/CalibrationSessionDto";
import { killModals } from "./shared/components/common/ModalBase";
import Globals from "./support/Globals";

export function getMainApp(): any {
    return Global.MainApp;
}

export default defineComponent({
    components: {
        Header,
        Footer,
        AlertDialog
    },
    created(): void {
        Global.MainApp = this;
    },
    mounted(): void {
        initializeAlertDialog(this.$refs.alertDialog as any);
        Tooltip.initializeTooltips();
        DropDownMenuSupport.initialize();

        // Init signalr
        SignalR.start(Globals.GetSignalRFunction());

        DataRequest.getWebAPI = (): string => {
            return Globals.GetWebApi();
        };
        DataRequest.setRequestHeaders = (request: XMLHttpRequest, useAuthentication: boolean): void => {
            request.setRequestHeader("Management-Api-Version", "v1.0");
            const token = Access.getToken();
            if (useAuthentication && token) { // pass our JWT token
                request.setRequestHeader("Authorization", `Bearer ${token}`);
            }
        };
        DataRequest.getToken = (): string => {
            return Access.getToken() || "";
        };
        DataRequest.haveToken = (): boolean => {
            return Access.getToken() != null;
        };
        DataRequest.getMaxRetries = (): number => {
            return 3;
        };

        SignalR.onTeamViewerStarted = (deviceName: string, techGuid: string|null): void => {
            if (!techGuid || techGuid === CurrentUser.userGuid)
                Toast.info(`Remote Assistance has been enabled on device ${deviceName}.`);
        };
        SignalR.onCalibrationCanceledByDevice = (): void => {
            Toast.warning("The calibration has been canceled by the customer.");
            getMainApp().routerPush("/");
        };
        SignalR.onCalibrationDisconnectedDevice = (): void => {
            Toast.warning("The device has disconnected and the calibration has been canceled.");
            getMainApp().routerPush("/");
        };
        SignalR.onCalibrationAssumedByOtherTechnician = (name: string|null): void => {
            Toast.warning(`The session has taken over by ${name ?? "another technician"}.`);
            getMainApp().routerPush("/");
        };
    },
    unmounted(): void {
        SignalR.onTeamViewerStarted = null;
        SignalR.onCalibrationCanceledByDevice = null;
        SignalR.onCalibrationDisconnectedDevice = null;
    },
    methods: {
        getSessionInfo(sessionGuid: string): void {
            const dr = new DataRequest();
            dr.$get<CalibrationSessionDto>(`/Service/CalibrationSession/${sessionGuid}`)
                .then((session: CalibrationSessionDto): void => {
                    Global.CalibrationSession = session;
                })
                .catch((reason): void => {
                    confirmOk(`Unable to obtain session record - ${reason}.`);
                });
        },

        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        routerReplace(url: string, query?: any): void {
            // defeat vue router caching by adding a time tick.
            let complete = `${url}?x=${this.ticks()}`;
            if (query)
                complete += this.addQuery(query);
            killModals();// kill modals in case we go to a page that's already shown
            (this as any).$router.replace(complete);
        },

        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        routerPush(url: string, query?: any): void {
            this.routerReplace(url, query);
        },

        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        addQuery(query?: any): string {
            let args = "";
            for (const key in query) {
                args += "&";
                const val = query[key];
                if (val !== undefined && val !== null)
                    args += `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
                else
                    args += `${encodeURIComponent(key)}=`;
            }
            return args;
        },

        ticks(): string {
            const dNow = new Date();
            const dStart = new Date(1970, 0, 1);
            const seconds = dNow.getTime();
            const secondsStart = dStart.getTime();
            const dateDifference = seconds - secondsStart;
            return dateDifference.toString();
        },

        /**
         * Tech cancels session, with prompt.
         */
        cancelCalibration(): void {
            if (Global.CalibrationSession && Global.CalibrationSession.CalibrationSessionGuid) {
                confirmYesNo("Are you sure you want to cancel this session?")
                    .then((clicked: ButtonClicked) => {
                        if (clicked === ButtonClicked.Ok) {
                            this.abortCalibration();
                        }
                    });
            } else {
                this.abortCalibration();
            }
        },

        /**
         * Tech cancels session, without prompt.
         */
        abortCalibration(): void {
            if (Global.CalibrationSession && Global.CalibrationSession.CalibrationSessionGuid)
                CalibrationEventSupport.sendEvent(Global.CalibrationSession.CalibrationSessionGuid, CalibrationEvent.CalibrationTechnicianCanceled);
            Utility.pageChanged = false;
            Global.CalibrationSession = null;
            getMainApp().routerPush("/");
        },

        /**
         * Session end.
         */
        endCalibration(): void {
            CalibrationEventSupport.CancelRetryableEvents();
            if (Global.CalibrationSession && Global.CalibrationSession.DeviceGuid && CurrentUser.userGuid) {
                SignalR.connection.send("techEndCalibrationSession", CurrentUser.userGuid, Global.CalibrationSession.DeviceGuid);
            }
            Utility.pageChanged = false;
            getMainApp().routerPush("/");
        },
    },
});

</script>
